import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<button type=button class=flight__propeller-toggle title="Port extension in flight — the original Red Baron draws no propeller in any cockpit or chase view. Its own plane viewer does draw one, and this is it."data-testid=propeller-effect-toggle>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<button type=button class=flight__enhanced-effects-toggle title="Port extension — particle fire, smoke and hit sparks on damaged aeroplanes. Off, they burn the way the original drew it: a held EXPLO.BMP flame and dithered SMOKE.BMP puffs."data-testid=enhanced-effects-toggle>`),
  _tmpl$3 = /*#__PURE__*/_$template(`<button type=button class=flight__enhanced-models-toggle data-testid=enhanced-models-toggle>`);
/**
 * The three `EffectsSettings` toggles that are **not** about flying — PROPELLER
 * EFFECT, ENHANCED EFFECTS and ENHANCED MODELS — as components, so the two
 * screens that offer them offer the *same* ones.
 *
 * They were inline in `FlightSettingsControls.tsx` until VIEW AIRPLANES needed
 * them too (roadmap G15: "surface the ENHANCED MODELS / ENHANCED EFFECTS /
 * PROPELLER EFFECT toggles on that screen so the fleet can be inspected under
 * either model set with the propeller running"). Extracting them rather than
 * writing a second set is the same rule the bullet-sizing lesson states for
 * arithmetic and it applies just as well to a label: a control that exists
 * twice will eventually say two different things about what it does, and the
 * *title* attributes here are the part that carries the port-extension
 * disclosure. One copy, one disclosure.
 *
 * They are all still driven by the same persisted `EffectsSettings`
 * (`effects-settings.ts`), so a pilot who turns ENHANCED MODELS off while
 * inspecting the fleet takes off in the baked aeroplane he was just looking at.
 * That shared store *is* the feature, not an implementation detail of it.
 *
 * The rest of `EffectsSettings` — REALISTIC INSTRUMENTS, KILL CAMERA, ENHANCED
 * SKY, ENHANCED TERRAIN, STICK — is offered only on the PREFERENCES panel
 * (`shell/model/preferences-screen.ts`), because every one of them is about a
 * sortie and none of them has anything to decide on a parked aeroplane.
 */

import { enhancedIndex } from "../three/enhanced-catalog.ts";
export function PropellerEffectToggle(props) {
  return (() => {
    var _el$ = _tmpl$();
    _el$.$$click = () => props.onChange({
      ...props.settings,
      propellerEffect: !props.settings.propellerEffect
    });
    _$insert(_el$, () => props.settings.propellerEffect ? "PROPELLER EFFECT: ON" : "PROPELLER EFFECT: OFF");
    _$effect(() => _$setAttribute(_el$, "aria-pressed", props.settings.propellerEffect));
    return _el$;
  })();
}
export function EnhancedEffectsToggle(props) {
  return (() => {
    var _el$2 = _tmpl$2();
    _el$2.$$click = () => props.onChange({
      ...props.settings,
      enhancedEffects: !props.settings.enhancedEffects
    });
    _$insert(_el$2, () => props.settings.enhancedEffects ? "ENHANCED EFFECTS: ON" : "ENHANCED EFFECTS: OFF");
    _$effect(() => _$setAttribute(_el$2, "aria-pressed", props.settings.enhancedEffects));
    return _el$2;
  })();
}

/**
 * What ENHANCED MODELS says when the index came back empty — an install that
 * never ran `bun run enhanced`, or a deployment that cannot serve
 * `/enhanced/*` (`three/enhanced-diagnostics.ts`).
 *
 * This label is the fix for the report that found the second case. The switch
 * read `ENHANCED MODELS: ON`, clicking it changed nothing anywhere, and there
 * was no way to tell from the screen that there was nothing behind it. A
 * control that cannot change the picture has to say so; the setting itself is
 * still written, so a pilot who turns it off here has it off when the models
 * do arrive.
 */
export const ENHANCED_MODELS_UNAVAILABLE_LABEL = "ENHANCED MODELS: UNAVAILABLE";
export function EnhancedModelsToggle(props) {
  // `undefined` until the one manifest fetch lands, which is not the same as an
  // empty index and must not flash the unavailable label at a pilot who has the
  // models: only a *resolved* empty index is an answer.
  const nothingToSwitchTo = () => enhancedIndex()?.size === 0;
  return (() => {
    var _el$3 = _tmpl$3();
    _el$3.$$click = () => props.onChange({
      ...props.settings,
      enhancedModels: !props.settings.enhancedModels
    });
    _$insert(_el$3, (() => {
      var _c$ = _$memo(() => !!nothingToSwitchTo());
      return () => _c$() ? ENHANCED_MODELS_UNAVAILABLE_LABEL : props.settings.enhancedModels ? "ENHANCED MODELS: ON" : "ENHANCED MODELS: OFF";
    })());
    _$effect(_p$ => {
      var _v$ = nothingToSwitchTo() ? "No enhanced models are being served. Either this install never ran `bun run enhanced`, or /enhanced/* is not wired up on the host serving this build — the browser console names which." : "Port extension — aeroplanes and raid targets drawn from the offline-built enhanced models where one exists, and from the original's own .TBL geometry where it does not. Off, every one of them is exactly what the bake produced.",
        _v$2 = props.settings.enhancedModels;
      _v$ !== _p$.e && _$setAttribute(_el$3, "title", _p$.e = _v$);
      _v$2 !== _p$.t && _$setAttribute(_el$3, "aria-pressed", _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$3;
  })();
}
_$delegateEvents(["click"]);